import React from 'react';
import { Backdrop, Box, Fade, Typography, Zoom } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useOnboarding } from './OnboardingProvider';
import { useDurees } from './useReduceMotion';
import { chuteConfetti, pulseAnneau } from './animations';
import { VERT, VERT_CLAIR } from './tokens';

const DUREE_AFFICHAGE = 2200;
const COULEURS = ['#2e7d32', '#43a047', '#ff9800', '#ffffff'];

const TEXTES = {
  voyageur: {
    titre: 'Profil voyageur enregistré',
    corps: "Il ne reste plus qu'à remplir votre demande.",
    confettis: 12,
  },
  // Câblée au lot 4 : la validation d'une demande vit dans les 56 formulaires,
  // qui ne sont pas touchés par ce lot.
  demande: {
    titre: 'Votre demande est enregistrée',
    corps: 'Nous vérifions votre dossier et vous prévenons par email à chaque étape.',
    confettis: 18,
  },
};

/**
 * Accusé de réception, jamais un obstacle : un clic, la touche Échap ou la simple
 * attente la referment. Ne se joue qu'UNE fois par compte et par étape.
 *
 * Monté UNE SEULE FOIS dans App.tsx, sous OnboardingProvider et hors de <Routes> :
 * l'étape « voyageur » se termine en naviguant vers un formulaire de pays, où
 * MyTabs n'est pas monté. Un montage dans MyTabs la rendait injouable.
 */
const Celebration: React.FC = () => {
  const { celebration, fermerCelebration } = useOnboarding();
  const { reduce, d } = useDurees();

  React.useEffect(() => {
    if (!celebration) return;
    const t = setTimeout(fermerCelebration, DUREE_AFFICHAGE);
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fermerCelebration();
    };
    window.addEventListener('keydown', surTouche);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', surTouche);
    };
  }, [celebration, fermerCelebration]);

  if (!celebration) return null;
  const textes = TEXTES[celebration];

  return (
    <Backdrop
      open
      onClick={fermerCelebration}
      role="status"
      aria-live="polite"
      transitionDuration={d(200)}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.55)' }}
    >
      <Fade in timeout={d(200)}>
        <Box sx={{ textAlign: 'center', px: 3, maxWidth: 360 }}>
          <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
            {!reduce && (
              <Box
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  border: `2px solid ${VERT_CLAIR}`,
                  animation: `${pulseAnneau} 800ms ease-out 2`,
                }}
              />
            )}
            <Zoom in timeout={d(320)}>
              <Box
                sx={{
                  width: 84,
                  height: 84,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${VERT}, ${VERT_CLAIR})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckIcon sx={{ fontSize: 46, color: '#fff' }} />
              </Box>
            </Zoom>

            {!reduce &&
              Array.from({ length: textes.confettis }).map((_, i) => (
                <Box
                  key={i}
                  aria-hidden="true"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: `${(i / textes.confettis) * 100}%`,
                    width: 8,
                    height: 14,
                    borderRadius: '2px',
                    backgroundColor: COULEURS[i % COULEURS.length],
                    animation: `${chuteConfetti} 1100ms ease-in ${i * 50}ms 1 both`,
                  }}
                />
              ))}
          </Box>

          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.15rem', mb: 0.75 }}>
            {textes.titre}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', lineHeight: 1.5 }}>
            {textes.corps}
          </Typography>
        </Box>
      </Fade>
    </Backdrop>
  );
};

export default Celebration;
